import { useEffect, useState } from "react";
import { Loader2, Download } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type Range = "mtd" | "30d" | "90d" | "ytd";

const RANGE_LABEL: Record<Range, string> = {
  mtd: "Month to date",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  ytd: "Year to date",
};

type Report = {
  range: Range;
  flaggedCount: number;
  removedCount: number;
  auditCount: number;
  totalReviewed: number;
  byBrand: Array<{
    brandId: string;
    brandName: string;
    flagged: number;
    removed: number;
  }>;
  byMonth: Array<{ month: string; flagged: number; removed: number }>;
};

export default function DashboardReports() {
  return <AppShell>{() => <Inner />}</AppShell>;
}

function Inner() {
  const [range, setRange] = useState<Range>("30d");
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError(null);
    api<Report>(`/customer/reports?range=${range}`).then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setError(res.error ?? "Couldn't load report.");
        return;
      }
      setReport(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [range]);

  function downloadCsv() {
    const url = `/api/customer/reports/export.csv?range=${range}`;
    window.open(url, "_blank");
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Reports
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Flagged, takedowns, and removals across every brand on your account.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangePicker value={range} onChange={setRange} />
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!report}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-6 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!report && !error && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {report && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Flagged" value={report.flaggedCount} />
            <Stat label="Removed" value={report.removedCount} accent />
            <Stat label="Audits" value={report.auditCount} />
            <Stat label="Reviews scanned" value={report.totalReviewed} />
          </div>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Activity by month
            </h2>
            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              {report.byMonth.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
                  No activity in this range yet.
                </p>
              ) : (
                <MonthlyBars data={report.byMonth} />
              )}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              By brand
            </h2>
            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
              {report.byBrand.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
                  No brands yet. Add a brand to start tracking.
                </p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-3">Brand</th>
                      <th className="px-5 py-3 text-right">Flagged</th>
                      <th className="px-5 py-3 text-right">Removed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {report.byBrand.map((b) => (
                      <tr key={b.brandId}>
                        <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">
                          {b.brandName}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                          {b.flagged}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-emerald-700 dark:text-emerald-300">
                          {b.removed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function RangePicker({
  value,
  onChange,
}: {
  value: Range;
  onChange: (r: Range) => void;
}) {
  const ranges: Range[] = ["mtd", "30d", "90d", "ytd"];
  return (
    <div className="inline-flex rounded-full border border-slate-300 bg-white p-0.5 text-xs font-medium dark:border-slate-700 dark:bg-slate-900">
      {ranges.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() => onChange(r)}
          className={
            value === r
              ? "rounded-full bg-slate-900 px-3 py-1.5 text-white dark:bg-slate-100 dark:text-slate-900"
              : "rounded-full px-3 py-1.5 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          }
        >
          {RANGE_LABEL[r]}
        </button>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950/40"
          : "rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
      }
    >
      <div
        className={
          accent
            ? "text-xs font-medium text-emerald-700 dark:text-emerald-300"
            : "text-xs font-medium text-slate-500 dark:text-slate-400"
        }
      >
        {label}
      </div>
      <div
        className={
          accent
            ? "mt-2 text-3xl font-semibold tracking-tight text-emerald-900 dark:text-emerald-200"
            : "mt-2 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100"
        }
      >
        {value}
      </div>
    </div>
  );
}

function MonthlyBars({
  data,
}: {
  data: Array<{ month: string; flagged: number; removed: number }>;
}) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.flagged, d.removed)));
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {data.map((row) => (
        <div
          key={row.month}
          className="flex items-center gap-4 px-5 py-3 text-sm"
        >
          <div className="w-20 shrink-0 font-mono text-xs text-slate-500 dark:text-slate-400">
            {row.month}
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full bg-slate-900 dark:bg-slate-300"
                  style={{ width: `${(row.flagged / max) * 100}%` }}
                />
              </div>
              <div className="w-20 text-right text-xs text-slate-600 dark:text-slate-400">
                {row.flagged} flagged
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${(row.removed / max) * 100}%` }}
                />
              </div>
              <div className="w-20 text-right text-xs text-emerald-700 dark:text-emerald-400">
                {row.removed} removed
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
